import React, { useState } from 'react';
import { Copy, CheckCheck, Bot } from 'lucide-react';
import { formatDateRelative } from '@/lib/formatters';
import { renderSafeMarkdown } from '@/lib/safe-markdown';
import s from './ai.module.css';
import type { ConversationMessage } from '@/api/ai';

interface AIMessageProps {
  message: ConversationMessage;
}

// Very lightweight markdown-like renderer for AI responses
function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Heading
    if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className={s.mdH3}>{line.slice(3)}</h3>);
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={i} className={s.mdH2}>{line.slice(2)}</h2>);
    }
    // Bold text with ** or __
    else if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
      elements.push(<p key={i} className={s.mdBold}>{line.slice(2, -2)}</p>);
    }
    // Bullet list
    else if (line.startsWith('- ') || line.startsWith('* ')) {
      const items: string[] = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className={s.mdUl}>
          {items.map((item, j) => (
            <li key={j} className={s.mdLi}>
              <span className={s.mdLiDot} />
              <span>{renderSafeMarkdown(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }
    // Numbered list
    else if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className={s.mdOl}>
          {items.map((item, j) => (
            <li key={j} className={s.mdOlLi}>
              <span>{renderSafeMarkdown(item)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }
    // Empty line
    else if (line.trim() === '') {
      elements.push(<div key={i} className={s.mdSpacer} />);
    }
    // Regular paragraph
    else {
      elements.push(
        <p key={i} className={s.mdParagraph}>
          {renderSafeMarkdown(line)}
        </p>
      );
    }
    i++;
  }

  return <>{elements}</>;
}

// inlineMarkdown removed — replaced with renderSafeMarkdown for XSS prevention

export const AIMessage: React.FC<AIMessageProps> = ({ message }) => {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';

  async function handleCopy() {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (isUser) {
    return (
      <div className={s.messageGroup}>
        <div className={s.userBubbleRow}>
          <div style={{ maxWidth: '80%' }}>
            <div className={s.userBubble}>
              <p className={s.userBubbleText}>{message.content}</p>
            </div>
            {message.timestamp && (
              <div className={s.bubbleMetaRight}>
                <p className={s.bubbleTimestamp}>
                  {formatDateRelative(message.timestamp)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.messageGroup}>
      <div className={s.aiBubbleRow}>
        {/* AI Avatar */}
        <div className={s.botAvatarSm} style={{ marginTop: 2 }}>
          <Bot size={14} />
        </div>

        <div className={s.flex1} style={{ minWidth: 0 }}>
          <div className={s.aiBubble}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {renderMarkdown(message.content)}
            </div>
          </div>

          <div className={s.bubbleMeta}>
            {message.timestamp && (
              <p className={s.bubbleTimestamp}>
                {formatDateRelative(message.timestamp)}
              </p>
            )}
            <button
              onClick={handleCopy}
              className={s.copyBtn}
            >
              {copied ? <CheckCheck size={11} style={{ color: '#00D26A' }} /> : <Copy size={11} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
